/**
 * Small pieces the radiation screens (fieldiq-api#1334, RAD-1) draw the same
 * way: the status and leak-test badges, the Sources / Dosimetry strip, the
 * people picker's query and the source register/edit dialog.
 *
 * Badges are the register idiom — saturated signal fill, dark ink, uppercase —
 * and a key this module has no word for is a slate `—`, never a neighbour.
 */
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';
import {
  RAD_DOSIMETRY,
  RAD_FORM_LABEL,
  RAD_LEAK_FILL,
  RAD_LEAK_LABEL,
  RAD_LIST,
  RAD_STATUS_FILL,
  RAD_STATUS_LABEL,
  radErrorMessage,
  radLabel,
} from '../../lib/radiation';
import type { RadCatalog, RadSource, RadSourceWrite } from '../../types/radiation';
import { DH_INPUT, DH_SECONDARY, DhDialog, DhField, useOpenCount } from '../../pages/downhole/DownholeBits';

/** A refusal or failure under a form: the red-edged note the equipment screens use. */
export const RAD_ALERT = 'rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text';

function Badge({ fill, label }: { fill: string | undefined; label: string }) {
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${fill ?? 'bg-fi-signal-slate'}`}
    >
      {label}
    </span>
  );
}

export function RadStatusBadge({ status }: { status: string | null | undefined }) {
  const known = !!status && !!RAD_STATUS_LABEL[status];
  return <Badge fill={known ? RAD_STATUS_FILL[status!] : undefined} label={known ? RAD_STATUS_LABEL[status!] : '—'} />;
}

export function RadLeakBadge({ status }: { status: string | null | undefined }) {
  const known = !!status && !!RAD_LEAK_LABEL[status];
  return <Badge fill={known ? RAD_LEAK_FILL[status!] : undefined} label={known ? RAD_LEAK_LABEL[status!] : '—'} />;
}

/** Sources | Dosimetry — two views of one module, an underlined strip in the body. */
export function RadSectionNav() {
  const cls = ({ isActive }: { isActive: boolean }) =>
    `-mb-px border-b-2 px-1 pb-2 text-sm font-bold transition ${
      isActive ? 'border-fi-amber text-fieldiq-text' : 'border-transparent text-fieldiq-muted hover:text-fieldiq-text'
    }`;
  return (
    <nav aria-label="Radiation sections" className="flex gap-6 border-b border-fieldiq-border">
      <NavLink to={RAD_LIST} end className={cls}>Sources</NavLink>
      <NavLink to={RAD_DOSIMETRY} className={cls}>Dosimetry</NavLink>
    </nav>
  );
}

export interface RadPerson {
  id: string;
  name: string;
}

/** The tenant's people for a picker: customers and terminated users left out. */
export function useRadPeople(enabled: boolean) {
  return useQuery({
    queryKey: ['radiation', 'people'],
    queryFn: async (): Promise<RadPerson[]> =>
      unwrapArray<any>((await api.users.list()).data)
        .filter((u) => u?.id && u.role !== 'customer' && (u.employmentStatus ?? u.employment_status) !== 'terminated')
        .map((u) => ({
          id: u.id,
          name: [u.firstName ?? u.first_name, u.lastName ?? u.last_name].filter(Boolean).join(' ').trim() || u.email || '—',
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── register / edit a source ────────────────────────────────────────────────

interface SourceDraft {
  isotope: string;
  source_form: string;
  serial_number: string;
  holder_description: string;
  reference_activity: string;
  reference_on: string;
  licence_number: string;
  status: string;
  notes: string;
}

const draftOf = (s: RadSource | null): SourceDraft => ({
  isotope: s?.isotope ?? '',
  source_form: s?.sourceForm ?? '',
  serial_number: s?.serialNumber ?? '',
  holder_description: s?.holderDescription ?? '',
  reference_activity: s?.referenceActivity == null ? '' : String(s.referenceActivity),
  reference_on: s?.referenceOn ?? '',
  licence_number: s?.licenceNumber ?? '',
  status: s?.status ?? '',
  notes: s?.notes ?? '',
});

/** The body for a create, or an edit's changed keys, or an error sentence. */
export function radSourceBody(draft: SourceDraft, before: RadSource | null, activityUnit: string): RadSourceWrite | string {
  if (!draft.isotope) return 'Choose the isotope.';
  if (!draft.source_form) return 'Choose the source form.';
  if (!draft.serial_number.trim()) return 'Enter the serial number.';
  const act = Number(draft.reference_activity.trim());
  if (draft.reference_activity.trim() === '' || !Number.isFinite(act) || act <= 0) {
    return `Enter the reference activity in ${activityUnit || 'the tenant unit'}, greater than 0.`;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.reference_on)) return 'Enter the reference day.';
  const full: RadSourceWrite = {
    isotope: draft.isotope,
    source_form: draft.source_form,
    serial_number: draft.serial_number.trim(),
    holder_description: draft.holder_description.trim() || null,
    reference_activity: act,
    reference_on: draft.reference_on,
    licence_number: draft.licence_number.trim() || null,
    notes: draft.notes.trim() || null,
  };
  if (!before) return full;
  const was = draftOf(before);
  const out: RadSourceWrite = {};
  (Object.keys(full) as Array<keyof RadSourceWrite>).forEach((k) => {
    const prev = k === 'reference_activity' ? before.referenceActivity : (was as any)[k] || null;
    if ((full as any)[k] !== prev) (out as any)[k] = (full as any)[k];
  });
  if (draft.status && draft.status !== before.status) out.status = draft.status;
  return out;
}

export function RadSourceDialog({
  open,
  onClose,
  catalog,
  activityUnit,
  source,
  pending,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  catalog: RadCatalog | null | undefined;
  activityUnit: string;
  /** Null to register a new source. */
  source: RadSource | null;
  pending: boolean;
  onSubmit: (body: RadSourceWrite) => Promise<unknown>;
}) {
  const openCount = useOpenCount(open);
  const [draft, setDraft] = useState<SourceDraft>(draftOf(source));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setDraft(draftOf(source));
    setError(null);
    // Reset on open only, never on a refetch of the source (useOpenCount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);

  const set = (k: keyof SourceDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setDraft((d) => ({ ...d, [k]: e.target.value }));

  // A status reached through an event (lost, abandoned, disposed) is not offered:
  // the API refuses to set it here. The current one stays listed so it reads true.
  const statusOptions = catalog?.patchableStatuses ?? [];
  const statusEditable = !!source && (catalog?.patchableStatuses ?? []).includes(source.status);

  const submit = async () => {
    setError(null);
    const body = radSourceBody(draft, source, activityUnit);
    if (typeof body === 'string') return setError(body);
    if (source && Object.keys(body).length === 0) return onClose();
    try {
      await onSubmit(body);
      onClose();
    } catch (e) {
      setError(radErrorMessage(e, source ? 'The source could not be saved.' : 'The source could not be registered.'));
    }
  };

  return (
    <DhDialog
      open={open}
      title={source ? 'Edit source' : 'Register a source'}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" onClick={onClose} className={DH_SECONDARY}>Cancel</button>
          <button type="button" onClick={submit} disabled={pending} className="fi-action fi-action--standard fi-action--primary">
            {pending ? 'Saving…' : source ? 'Save' : 'Register'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <DhField label="Isotope">
          <select aria-label="Isotope" className={DH_INPUT} value={draft.isotope} onChange={set('isotope')}>
            <option value="">Choose…</option>
            {(catalog?.isotopes ?? []).map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
          </select>
        </DhField>
        <DhField label="Source form">
          <select aria-label="Source form" className={DH_INPUT} value={draft.source_form} onChange={set('source_form')}>
            <option value="">Choose…</option>
            {(catalog?.sourceForms ?? []).map((f) => <option key={f} value={f}>{radLabel(RAD_FORM_LABEL, f)}</option>)}
          </select>
        </DhField>
        <DhField label="Serial number">
          <input aria-label="Serial number" className={DH_INPUT} value={draft.serial_number} onChange={set('serial_number')} />
        </DhField>
        <DhField label="Licence number">
          <input aria-label="Licence number" className={DH_INPUT} value={draft.licence_number} onChange={set('licence_number')} />
        </DhField>
        <DhField label={`Reference activity (${activityUnit || '—'})`} hint="As on the source certificate.">
          <input aria-label="Reference activity" inputMode="decimal" className={DH_INPUT} value={draft.reference_activity} onChange={set('reference_activity')} />
        </DhField>
        <DhField label="Reference day">
          <input aria-label="Reference day" type="date" className={DH_INPUT} value={draft.reference_on} onChange={set('reference_on')} />
        </DhField>
        <DhField label="Holder / container">
          <input aria-label="Holder" className={DH_INPUT} value={draft.holder_description} onChange={set('holder_description')} />
        </DhField>
        {source && (
          <DhField label="Status" hint={statusEditable ? undefined : 'Changed only by recording an event.'}>
            <select aria-label="Status" className={DH_INPUT} value={draft.status} onChange={set('status')} disabled={!statusEditable}>
              {!statusEditable && <option value={source.status}>{RAD_STATUS_LABEL[source.status] ?? '—'}</option>}
              {statusEditable && statusOptions.map((s) => <option key={s} value={s}>{RAD_STATUS_LABEL[s] ?? '—'}</option>)}
            </select>
          </DhField>
        )}
      </div>
      <div className="mt-4">
        <DhField label="Notes">
          <textarea aria-label="Notes" rows={2} className={`${DH_INPUT} h-auto py-2`} value={draft.notes} onChange={set('notes')} />
        </DhField>
      </div>
      {error && <p role="alert" className={`mt-3 ${RAD_ALERT}`}>{error}</p>}
    </DhDialog>
  );
}
